import { useEffect, useState } from "react";
import { Sparkles, Flame, Award, Gift, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const LEVELS = ["Starter", "Bronze", "Silver", "Gold", "Platinum", "Diamond"];

export default function RewardsPage() {
  const [stats, setStats] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const load = async () => {
    const { data } = await api.get("/me/stats");
    setStats(data);
  };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/rewards/daily-bonus");
      toast.success(`${data.message} · Streak ${data.streak}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim");
    } finally { setClaiming(false); }
  };

  const redeem = async (kind) => {
    try { await api.post("/rewards/redeem", { kind }); toast.success("Redeemed!"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not redeem"); }
  };

  const levelIdx = Math.max(0, LEVELS.indexOf(stats?.level || "Starter"));

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Rewards</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Rewards & Bonuses</h1>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Coins className="h-4 w-4 text-primary" /> Points</div>
          <div className="mt-2 font-display text-4xl text-primary">{stats?.points ?? 0}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Award className="h-4 w-4 text-accent" /> Level</div>
          <div className="mt-2 font-display text-4xl">{stats?.level ?? "Starter"}</div>
          <div className="mt-3 flex gap-1">
            {LEVELS.map((l, i) => (
              <div key={l} className={`h-1.5 flex-1 rounded-full ${i <= levelIdx ? "bg-accent" : "bg-secondary"}`} />
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Flame className="h-4 w-4 text-accent" /> Daily streak</div>
          <div className="mt-2 font-display text-4xl">{stats?.streak ?? 0} <span className="text-base text-muted-foreground">days</span></div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-primary bg-card p-6 glow-cobalt" data-testid="daily-bonus-card">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <div className="font-display text-xl">Claim your daily bonus</div>
            <p className="mt-1 text-sm text-muted-foreground">Come back every day to keep your streak alive. +1 point each claim.</p>
          </div>
          <Button onClick={claim} disabled={claiming} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="rewards-claim-btn">
            <Sparkles className="mr-2 h-4 w-4" /> {claiming ? "Claiming…" : "Claim now"}
          </Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-lg">Zuntria Credits</h2>
          <div className="mt-2 font-display text-3xl text-accent">{stats?.zuntria_credits ?? 0}</div>
          <Button className="mt-4" variant="outline" onClick={async () => { try { const { data } = await api.post("/zcredits/redeem"); toast.success(`Redeemed! +5 AI credits (${data.zuntria_credits} Z-Credits left)`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Redeem failed"); } }} data-testid="rewards-redeem-zcredits">Redeem 20 → 5 AI credits</Button>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-lg flex items-center gap-2"><Gift className="h-4 w-4 text-primary" /> Gifts</h2>
          <div className="mt-2 font-display text-3xl">{stats?.gifts ?? 0}</div>
          <Button className="mt-4" variant="outline" onClick={() => redeem("gift")} data-testid="rewards-redeem-gift">Redeem 1 gift → 6 days Premium</Button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-lg">Redeem points</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button variant="outline" onClick={() => redeem("points-cash")} data-testid="rewards-redeem-points-cash">50 points → 6 days Premium</Button>
          <Button variant="outline" onClick={() => redeem("points-gift")} data-testid="rewards-redeem-points-gift">100 points → 1 gift</Button>
        </div>
      </div>
    </div>
  );
}
